import { FIELDS } from 'modules/cm/fields/types';
import {
  DataDirector,
  LiabilityFormData,
  LiabilityRequestData,
  LiabilityTypeBuilder,
  LiabilityTypeLabelBuilder,
} from './types';

export class CommonType implements DataDirector {
  protected resultBuilder: LiabilityTypeBuilder;
  protected labelBuilder: LiabilityTypeLabelBuilder;

  constructor(builder: LiabilityTypeBuilder, labelBuilder: LiabilityTypeLabelBuilder) {
    this.resultBuilder = builder;
    this.labelBuilder = labelBuilder;
  }

  public prepareData(expenditure: LiabilityFormData) {
    this.resultBuilder.formData = expenditure;
    const allowedFieldValueSetters = new Map<FIELDS, () => void>([
      [FIELDS.EXPENDITURE_TYPE, this.resultBuilder.setType.bind(this.resultBuilder)],
      [FIELDS.EXPENDITURE_AMOUNT, this.resultBuilder.setAmount.bind(this.resultBuilder)],
      [FIELDS.EXPENDITURE_END_DATE, this.resultBuilder.setEndDate.bind(this.resultBuilder)],
      [FIELDS.EXPENDITURE_BALANCE, this.resultBuilder.setCreditCardBalance.bind(this.resultBuilder)],
      [
        FIELDS.EXPENDITURE_WILL_BE_CLEARED_BEFORE_LOAN,
        this.resultBuilder.setIsClearedBeforeLoanCompletes.bind(this.resultBuilder),
      ],
      [
        FIELDS.EXPENDITURE_WILL_BE_CLEARED_BY_LOAN,
        this.resultBuilder.setIsClearedByLoanCompletes.bind(this.resultBuilder),
      ],
      [FIELDS.EXPENDITURE_ID, this.resultBuilder.setId.bind(this.resultBuilder)],
      [FIELDS.EXPENDITURE_FREQUENCY, this.resultBuilder.setFrequency.bind(this.resultBuilder)],
      [
        FIELDS.EXPENDITURE_TOTAL_NUMBER_OF_PROPERTIES,
        this.resultBuilder.setTotalNumberOfProperties.bind(this.resultBuilder),
      ],
      [FIELDS.EXPENDITURE_DESCRIPTION, this.resultBuilder.setDescription.bind(this.resultBuilder)],
      [FIELDS.EXPENDITURE_CREDIT_LIMIT, this.resultBuilder.setCreditLimit.bind(this.resultBuilder)],
    ]);
    const allowedFields = this.getAllowedFields();

    allowedFields.forEach((allowedField) => {
      const setter = allowedFieldValueSetters.get(allowedField);
      if (setter) {
        setter();
      }
    });
  }

  getData(): LiabilityRequestData {
    return this.resultBuilder.getData();
  }

  getAllowedFields() {
    return [FIELDS.EXPENDITURE_TYPE, FIELDS.EXPENDITURE_ID];
  }

  getTitle(field: keyof LiabilityFormData) {
    return this.labelBuilder.getLabel(field);
  }
}

export class CreditCard extends CommonType {
  getAllowedFields() {
    return super
      .getAllowedFields()
      .concat([
        FIELDS.EXPENDITURE_BALANCE,
        FIELDS.EXPENDITURE_CREDIT_LIMIT,
        FIELDS.EXPENDITURE_WILL_BE_CLEARED_BEFORE_LOAN,
        FIELDS.EXPENDITURE_AMOUNT,
        FIELDS.EXPENDITURE_WILL_BE_CLEARED_BY_LOAN,
      ]);
  }
}

export class UnsecuredLoan extends CommonType {
  constructor(builder: LiabilityTypeBuilder, labelBuilder: LiabilityTypeLabelBuilder) {
    super(builder, labelBuilder);
    this.labelBuilder.setCreditCardBalance('Loan balance');
  }

  getAllowedFields() {
    return super
      .getAllowedFields()
      .concat([
        FIELDS.EXPENDITURE_AMOUNT,
        FIELDS.EXPENDITURE_WILL_BE_CLEARED_BEFORE_LOAN,
        FIELDS.EXPENDITURE_WILL_BE_CLEARED_BY_LOAN,
        FIELDS.EXPENDITURE_BALANCE,
        FIELDS.EXPENDITURE_FREQUENCY,
        FIELDS.EXPENDITURE_END_DATE,
      ]);
  }
}

export class StudentsLoan extends CommonType {
  getAllowedFields() {
    return super
      .getAllowedFields()
      .concat([
        FIELDS.EXPENDITURE_AMOUNT,
        FIELDS.EXPENDITURE_END_DATE,
        FIELDS.EXPENDITURE_WILL_BE_CLEARED_BEFORE_LOAN,
        FIELDS.EXPENDITURE_FREQUENCY,
      ]);
  }
}

export class Other extends CommonType {
  getAllowedFields() {
    return super
      .getAllowedFields()
      .concat([
        FIELDS.EXPENDITURE_AMOUNT,
        FIELDS.EXPENDITURE_WILL_BE_CLEARED_BEFORE_LOAN,
        FIELDS.EXPENDITURE_WILL_BE_CLEARED_BY_LOAN,
        FIELDS.EXPENDITURE_FREQUENCY,
        FIELDS.EXPENDITURE_DESCRIPTION,
        FIELDS.EXPENDITURE_END_DATE,
      ]);
  }
}

export class PensionContribution extends CommonType {
  getAllowedFields() {
    return super
      .getAllowedFields()
      .concat([FIELDS.EXPENDITURE_AMOUNT, FIELDS.EXPENDITURE_END_DATE, FIELDS.EXPENDITURE_FREQUENCY]);
  }
}

export class BuyToLetMortgage extends CommonType {
  constructor(builder: LiabilityTypeBuilder, labelBuilder: LiabilityTypeLabelBuilder) {
    super(builder, labelBuilder);
    this.labelBuilder.setCreditCardBalance('Mortgage balance');
  }
  getAllowedFields() {
    return super
      .getAllowedFields()
      .concat([
        FIELDS.EXPENDITURE_AMOUNT,
        FIELDS.EXPENDITURE_BALANCE,
        FIELDS.EXPENDITURE_FREQUENCY,
        FIELDS.EXPENDITURE_TOTAL_NUMBER_OF_PROPERTIES,
      ]);
  }
}

export class MaintenancePayments extends CommonType {
  getAllowedFields() {
    return super
      .getAllowedFields()
      .concat([FIELDS.EXPENDITURE_AMOUNT, FIELDS.EXPENDITURE_END_DATE, FIELDS.EXPENDITURE_FREQUENCY]);
  }
}
